import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

export interface SavedStory {
  text: string;
  revision: number;
}

/**
 * A module story, or one lesson's beat in it — CURR-39. The revision a save sends is the
 * one the author's edit started from, captured at the first keystroke: read live, a
 * background refetch would quietly turn a stale save into an overwrite. A refused save
 * keeps the typed text, and Reload shows the saved version beside it before anything
 * replaces it.
 */
export function StoryEditor({
  action,
  label,
  helperText,
  minRows,
  required = false,
  maxLength,
  saved,
  save,
  load,
}: {
  /** What the action feed calls the save. */
  action: string;
  label: string;
  helperText?: string;
  minRows: number;
  /** A module story cannot be saved empty; a beat can. */
  required?: boolean;
  /** Counted under the field, and a longer text cannot be saved. */
  maxLength?: number;
  saved: SavedStory;
  save: (text: string, revision: number) => Promise<unknown>;
  load: () => Promise<SavedStory>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [base, setBase] = useState<number | null>(null);
  const [latest, setLatest] = useState<SavedStory | null>(null);
  const text = draft ?? saved.text;
  const dirty = draft !== null && draft !== saved.text;
  const tooLong = maxLength !== undefined && text.length > maxLength;
  const discard = (): void => {
    setDraft(null);
    setBase(null);
    setLatest(null);
  };

  const write = useTrackedMutation(action, {
    mutationFn: () => save(text, base ?? saved.revision),
    onSuccess: discard,
  });
  const reload = useTrackedMutation(`Reload ${label.toLowerCase()}`, {
    mutationFn: load,
    onSuccess: (story) => {
      setLatest(story);
      setBase(story.revision);
      write.reset();
    },
  });

  return (
    <Stack
      component="form"
      spacing={1}
      onSubmit={(event) => {
        event.preventDefault();
        write.mutate();
      }}
    >
      <TextField
        multiline
        fullWidth
        size="small"
        minRows={minRows}
        label={label}
        helperText={
          maxLength === undefined
            ? helperText
            : `${helperText ?? ''} ${String(text.length)} / ${String(maxLength)} characters.`.trim()
        }
        error={tooLong}
        value={text}
        onChange={(event) => {
          setBase((was) => was ?? saved.revision);
          setDraft(event.target.value);
        }}
      />
      {write.isError && (
        <Alert
          severity="error"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                disabled={write.isPending}
                onClick={() => {
                  write.mutate();
                }}
              >
                Retry
              </Button>
              <Button
                color="inherit"
                size="small"
                disabled={reload.isPending}
                onClick={() => {
                  reload.mutate();
                }}
              >
                Reload
              </Button>
            </Stack>
          }
        >
          {describeApiError(write.error)} Your text is still here.
        </Alert>
      )}
      {reload.isError && (
        <Alert severity="error">{describeApiError(reload.error)}</Alert>
      )}
      {latest !== null && (
        <Alert
          severity="info"
          aria-label="Saved version"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setDraft(latest.text);
                setLatest(null);
              }}
            >
              Use saved version
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Saved version, revision {latest.revision}. Saving now replaces it
            with your text.
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {latest.text === '' ? '(empty)' : latest.text}
          </Typography>
        </Alert>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={
            !dirty ||
            tooLong ||
            write.isPending ||
            (required && text.trim() === '')
          }
        >
          Save {label.toLowerCase()}
        </Button>
        <Button
          size="small"
          disabled={draft === null || write.isPending}
          onClick={() => {
            discard();
            write.reset();
          }}
        >
          Undo changes
        </Button>
      </Stack>
    </Stack>
  );
}
